/**
 * Sign up — two fields, and nothing that delays a first scored lead.
 *
 * Visual Reference: `design/screens/auth-signup.html` (default · loading ·
 * error). Its own note is the specification: "Two fields is the whole form: no
 * company name, no phone, nothing that delays a first scored lead."
 *
 * ARCH §5.1 and DECISION-036: no organisation name is collected. It is derived
 * from the email domain by the API and renamed in settings.
 *
 * Story 3.3.
 */
import { useState, type FormEvent } from 'react'
import { AuthFootnote, AuthHeading, AuthLink, AuthPanel, AuthPoint, AuthEyebrow } from './AuthPanel'
import { Button } from '../ui/Button'
import { Callout } from '../ui/Callout'
import { Field } from '../ui/Field'
import { apiPost, ApiFailure } from '../lib/api'
import { DPA_OPEN, siteUrl } from '../lib/site'

/** PRD §5.1 — ten characters, no composition rules. */
const PASSWORD_MIN_LENGTH = 10

/**
 * The strength meter gives ADVICE, not a score.
 *
 * ARCH §5.1: "the `settings-profile` mockup's strength meter gives advice
 * ('Add another word'), not a score, and three unrelated words beats one clever
 * substitution." So the bars count words, not character classes — a meter that
 * rewarded a symbol would be teaching the opposite of the password rule.
 */
function strength(password: string): { filled: number; advice: string } {
  const words = password.trim().split(/\s+/).filter(Boolean).length
  if (password.length < PASSWORD_MIN_LENGTH) {
    return { filled: 0, advice: 'Use 10 characters or more. A short phrase is stronger than a scramble.' }
  }
  if (words >= 3) return { filled: 3, advice: 'Strong — a phrase like that takes years to guess' }
  if (words === 2) return { filled: 2, advice: 'Good. Add another word and it gets much harder to guess.' }
  return { filled: 1, advice: 'Add another word — three unrelated words beats one clever substitution.' }
}

export function SignUp() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)

  const meter = strength(password)
  // `auth-signup.html` renders the duplicate-address error AT THE FIELD, with
  // the way out beside it: "That email is already registered. Log in instead?"
  const emailError = failure?.code === 'conflict' ? failure.message : undefined
  const formError = failure && failure.code !== 'conflict' ? failure.message : undefined

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setFailure(null)
    try {
      await apiPost('/v1/auth/sign-up', { email, password })
      /**
       * `send=1` asks the verify screen to SEND one, and that is a different
       * claim from the `sent=1` this used to pass.
       *
       * Sign-up mints six rows and signs the person in; it does not send mail.
       * Better Auth's `sendOnSignUp` is off (`services/auth`), and this route is
       * Leadgible's own, so no verification email exists at this point. `sent=1`
       * asserted one had gone and started the 30-second cooldown on the only
       * button that would have sent it — so the screen said "We sent a link to
       * you@company.com" and greyed out the fix, and nothing ever arrived.
       *
       * The send is not moved into sign-up because it must not be in front of
       * the 201: the one production send measured took 30.9 seconds of wall
       * time, which is a sign-up that looks broken. It happens on the next
       * screen instead, where `auth-verify.html` already draws the states for
       * it — loading while it goes, the cooldown after, the error Callout if it
       * fails.
       *
       * fixes: a sign-up on production at 02:29:54Z sent no mail and no request
       * followed it; the account sat unverified with the screen claiming
       * otherwise. 2026-09-08
       */
      window.location.assign(`/verify?email=${encodeURIComponent(email)}&send=1`)
    } catch (error) {
      setFailure(error instanceof ApiFailure ? error : null)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthPanel
      aside={
        <>
          <AuthEyebrow>What you get on day one</AuthEyebrow>
          <div className="flex flex-col gap-mdx-18">
            <AuthPoint>See why every lead scored what it did</AuthPoint>
            <AuthPoint>Your funnel never stops collecting, even at your limit</AuthPoint>
            <AuthPoint>Works on any website with one line of code</AuthPoint>
          </div>
        </>
      }
    >
      <AuthHeading title="Start qualifying leads today.">
        Free for 100 submissions a month. No card needed.
      </AuthHeading>

      <form className="flex flex-col gap-mdx-20" onSubmit={submit} noValidate>
        {formError ? <Callout>{formError}</Callout> : null}

        <Field
          label="Work email"
          type="email"
          name="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={
            emailError ? (
              <>
                {emailError} <AuthLink href="/login">Log in instead?</AuthLink>
              </>
            ) : undefined
          }
        />

        <Field
          label="Password"
          type="password"
          name="password"
          autoComplete="new-password"
          placeholder="At least 10 characters"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          helper={
            <span className="flex flex-wrap items-center gap-x-mdx-10 gap-y-mdx-6">
              <span aria-hidden="true" className="flex shrink-0 gap-mdx-4">
                {[0, 1, 2].map((bar) => (
                  <span
                    key={bar}
                    className={`h-mdx-4 w-mdx-22 rounded-mdx-sm ${
                      bar < meter.filled ? 'bg-mdx-hot' : 'bg-mdx-line'
                    }`}
                  />
                ))}
              </span>
              <span>{meter.advice}</span>
            </span>
          }
        />

        <Button type="submit" full loading={submitting ? 'Creating your account' : undefined}>
          Create account
        </Button>

        {/*
          Story 3.10, DECISION-102 (Q-070). This paragraph made three promises
          and kept none: `/terms`, `/privacy` and `/dpa` were paths on THIS
          origin, which serves an SPA and has none of them.

          Two became real on the marketing site, where Story 13.7 built them.
          The third sentence — "our Data Processing Agreement is here to sign
          now" — was CUT from 2026-08-31 rather than pointed at a placeholder:
          PRD §5.9 dates the DPA at M3 ("available at signup, not on request"),
          and a stub page promising a document is the same false claim one hop
          further away. The mockup's own hrefs are `#terms`, `#privacy` and
          `#dpa` — placeholders that never named a destination — so it is
          silent on where these go, and could not be followed on whether a
          document exists.

          Story 26.3 brought it back, as `design/screens/auth-signup.html` draws
          it, on 2026-09-29: `DPA_OPEN` mirrors the site's switch, which opened
          only on the operator's approval of the agreement's exact text
          (DECISION-174, -176), and `tests/signup-legal-links.test.ts` holds the
          two switches equal — so the form never links a page the site does not
          build. The link is to the apex in every environment (`build.mjs`).
        */}
        <p className="m-0 max-w-mdx-measure-56 text-mdx-13 text-mdx-slate">
          By creating an account you agree to our{' '}
          <AuthLink href={siteUrl('/terms')} external>
            Terms
          </AuthLink>{' '}
          and{' '}
          <AuthLink href={siteUrl('/privacy')} external>
            Privacy Policy
          </AuthLink>
          .
          {DPA_OPEN ? (
            <>
              {' '}Handling client data? Our{' '}
              <AuthLink href={siteUrl('/dpa')} external>
                Data Processing Agreement
              </AuthLink>{' '}
              is here to sign now.
            </>
          ) : null}
        </p>

        <AuthFootnote>
          Already have an account? <AuthLink href="/login">Log in</AuthLink>
        </AuthFootnote>
      </form>
    </AuthPanel>
  )
}
